import capitalize from 'lodash/capitalize';
import PropertyTypes from 'constants/propertyTypes';
import ListingTypes from 'constants/listingTypes';
import AnalyticsGateway from 'lib/AnalyticsGateway';
import { LABELS, SALES, RENTALS, LISTINGS } from 'constants/comparableTypes';
import { getDateRangeFromMonths } from 'lib/SearchFilterUtil';
import {
    EXTERNAL_CHECKBOX_FILTER_LABELS,
    FREQUENCIES,
    EXTERNAL_TEXTFIELD_FILTER_LABELS,
    FRONTAGE_FILTER_LABELS, COMMERCIAL_RANGE_LABELS, DISTANCE_LABELS, INTERNAL_CHECKBOX_FILTER_LABELS,
    INTERNAL_RANGE_FILTER_LABELS, RURAL_CHECKBOX_FILTER_LABELS, RURAL_RANGE_FILTER_LABELS, SERVICE_FILTER_LABELS,
} from 'constants/filterOptions';
import { isNZLocale } from 'lib/PropertyUtils';
import { EXTERNAL_TEXTBOX_LABEL } from 'constants/advancedFilters';
import AnalyticsProperties from 'constants/analyticsProperties';

const ANY = 'ANY';
const buildTypeFilterEventProperties = (selectedTypes, types) => {
    if (!selectedTypes.length || selectedTypes.length === types.length) return [ANY];
    return selectedTypes;
};

const buildPropertyTypeProperties = (selectedTypes) =>
    buildTypeFilterEventProperties(selectedTypes, PropertyTypes);

const buildListingTypeProperties = (selectedTypes) =>
    buildTypeFilterEventProperties(selectedTypes, ListingTypes);

const buildRangeFilterProperty = (range, labelMin, labelMax) => {
    return [
        {label: labelMin, value: range.min || false},
        {label: labelMax, value: range.max || false}
    ];
};

const buildRangeFilterPropertyWithUnit = (range, labelMin, labelMax, labelUnit) => {
    const rangeProperty = buildRangeFilterProperty(range, labelMin, labelMax);
    return [
        ...rangeProperty,
        {label: labelUnit, value: range.unit || 'm²'},
    ];
};

const buildGenericDateRangeFilterProperty = (dateRange, label, defaultDate) => {
    if (dateRange === defaultDate || !dateRange ) {
        return [
            {label: `${label} Min`, value: false},
            {label: `${label} Max`, value: false},
        ];
    }
    const [min, max] = dateRange.split(' - ');
    return [
        {label: `${label} Min`, value: min},
        {label: `${label} Max`, value: max},
    ];
};

const buildDateRangeFilterProperty = (comparableType, dateRange) => {
    const defaultDate = {
        [SALES]: getDateRangeFromMonths(6),
        [RENTALS]: getDateRangeFromMonths(3),
        [LISTINGS]: getDateRangeFromMonths(3),
    }[comparableType];

    const dateLabel = `${LABELS[comparableType]} Date`;
    return buildGenericDateRangeFilterProperty(dateRange, dateLabel, defaultDate);
};

const buildRentalFrequencyProperty = (rentalFrequency, comparableType) => {
    return comparableType === RENTALS
        ? {label: 'Rental Period', value: rentalFrequency ? capitalize(rentalFrequency) : FREQUENCIES.week}
        : null;
};

const buildMultiselectProperty = (selectedOptions, label) => {
    if (!selectedOptions.length) return { label, value: [ANY] };
    return { label, value: selectedOptions };
};
const buildKeywordProperty = (keywords) => {
    return { label: 'Keyword Search', value: keywords.length ? keywords : false };
};

const buildExcludeAAProperty = (comparableType, filter) => {
    return comparableType === SALES
        ? { label: 'Exclude AA', value: filter }
        : null;
};

const buildExcludeRelatedPartiesProperty = (comparableType, filter) => {
    return comparableType === SALES
        ? { label: 'Exclude Related Parties', value: filter }
        : null;
};

const buildShowActiveListingsForSaleProperty = (comparableType, filter) => {
    return comparableType === ListingTypes
        ? { label: 'Show Active Listings', value: filter }
        : null;
};

const buildShowActiveListingsForRentProperty = (comparableType, filter) => {
    return comparableType === RENTALS
        ? { label: 'Show Active Listings', value: filter }
        : null;
};

const buildExternalCheckboxFilterProperties = (filters) => {
    return Object.keys(EXTERNAL_CHECKBOX_FILTER_LABELS)
        .map((key) => ({
            label: EXTERNAL_CHECKBOX_FILTER_LABELS[key],
            value: filters[key],
        }));
};

const buildExternalTextFieldFilterProperties = (filters) => {
    const filtersKey = Object.keys(EXTERNAL_TEXTFIELD_FILTER_LABELS);
    return Object.keys(EXTERNAL_TEXTBOX_LABEL).map((key, index) => ({
        label: EXTERNAL_TEXTBOX_LABEL[key],
        value: filters[filtersKey[index]] || false,
    }));
};

const buildCommercialFilterProperty = (filters) => [
    { label: 'Fee Code', value: filters.feeCode || false },
    ...Object.keys(COMMERCIAL_RANGE_LABELS).flatMap((key) =>
        buildRangeFilterPropertyWithUnit(filters[key],
            COMMERCIAL_RANGE_LABELS[key] + ' Min',
            COMMERCIAL_RANGE_LABELS[key] + ' Max',
            COMMERCIAL_RANGE_LABELS[key] + ' Unit'
        )
    ),
];

const buildDistanceFilterProperty = (filters) => Object
    .keys(DISTANCE_LABELS)
    .flatMap((key) =>
        buildRangeFilterProperty(filters[key],
            DISTANCE_LABELS[key] + ' Min',
            DISTANCE_LABELS[key] + ' Max',
        )
    );

const buildInternalFilterProperty = (filters) => [
    ...Object.keys(INTERNAL_CHECKBOX_FILTER_LABELS).map(key => ({
        label: INTERNAL_CHECKBOX_FILTER_LABELS[key],
        value: filters[key],
    })),
    ...Object.keys(INTERNAL_RANGE_FILTER_LABELS).flatMap(key => buildRangeFilterProperty(
        filters[key],
        INTERNAL_RANGE_FILTER_LABELS[key] + ' Min',
        INTERNAL_RANGE_FILTER_LABELS[key] + ' Max',
    ))
];

const buildPropertyValuesFilterProperty = (filters) => [
    ...buildRangeFilterProperty(filters.currentUnimprovedLandValue, 'Current Unimproved Land Value Min', 'Current Unimproved Land Value Max'),
    ...buildRangeFilterProperty(filters.unimprovedCapitalValue, 'Unimproved Capital Value Min', 'Unimproved Capital Value Max'),
    ...buildGenericDateRangeFilterProperty(filters.unimprovedCapitalValueDate, 'Unimproved Capital Value Date'),
    ...buildRangeFilterProperty(filters.latestConsumerSaleEstimate, 'Valuation Estimate Price Min', 'Valuation Estimate Price Max'),
    buildMultiselectProperty(filters.latestConsumerSaleConfidence, 'Valuation Estimate Confidence'),
];

const buildPropertyValuesFilterPropertyNz = (filters) => [
    ...buildRangeFilterProperty(filters.valuationCapitalValue, 'Capital Value Min', 'Capital Value Max'),
    ...buildGenericDateRangeFilterProperty(filters.valuationAssessmentDate, 'Valuation Date'),
    ...buildRangeFilterProperty(filters.valuationLandValue, 'Land Value Min', 'Land Value Max'),
    ...buildRangeFilterProperty(filters.valuationImprovementsValue, 'Improvements Value Min', 'Improvements Value Max'),
];

const buildRuralFilterProperty = (filters) => [
    ...Object.keys(RURAL_CHECKBOX_FILTER_LABELS).map(key => ({
        label: RURAL_CHECKBOX_FILTER_LABELS[key],
        value: filters[key],
    })),
    ...Object.keys(RURAL_RANGE_FILTER_LABELS).flatMap(key => buildRangeFilterProperty(
        filters[key],
        RURAL_RANGE_FILTER_LABELS[key] + ' Min',
        RURAL_RANGE_FILTER_LABELS[key] + ' Max',
    )),
];

const buildServiceFilterProperty = (filters) => Object.keys(SERVICE_FILTER_LABELS).map(key => ({
        label: SERVICE_FILTER_LABELS[key],
        value: filters[key],
    }));

const buildStructureFilterProperty = (filters) => [
    ...buildRangeFilterPropertyWithUnit(filters.buildingArea, 'Building Area Min', 'Building Area Max', 'Building Area Unit'),
    ...buildRangeFilterPropertyWithUnit(filters.equivalentBuildingArea, 'Equivalent Building Area Min', 'Equivalent Building Area Max', 'Equivalent Building Area Unit'),
    ...buildRangeFilterProperty(filters.floorLevelOfUnit, 'Floor Level of Unit Min', 'Floor Level of Unit Max'),
    ...buildRangeFilterProperty(filters.floorLevelsInsideUnit, 'No. Floor Levels Inside Unit Min', 'No. Floor Levels Inside Unit Max'),
    ...buildRangeFilterProperty(filters.flats, 'Number of Flats Min', 'Number of Flats Max'),
    ...buildRangeFilterProperty(filters.totalFloorsInBuilding, 'Total Floors in Building Min', 'Total Floors in Building Max'),
    ...buildRangeFilterProperty(filters.streetFrontage, 'Street Frontage Min', 'Street Frontage Max'),
    ...buildRangeFilterProperty(filters.yearBuildingRefurbished, 'Year Building Refurbished Min', 'Year Building Refurbished Max'),
    ...buildRangeFilterProperty(filters.yearEstablished, 'Year Established Min', 'Year Established Max'),
    { label: 'Free Standing Building', value: filters.freeStandingBuilding },

];

const buildExternalFilterProperty = (filters) => [
    ...buildExternalCheckboxFilterProperties(filters),
    ...buildExternalTextFieldFilterProperties(filters),
    ...buildRangeFilterProperty(filters.lockupGarages, 'Lockup Garages Min', 'Lockup Garages Max'),
    ...buildRangeFilterProperty(filters.noOfTennisCourts, 'Tennis Courts Min', 'Tennis Courts Max'),
];

const buildFrontageFilterProperty = (filters) => Object
    .keys(FRONTAGE_FILTER_LABELS)
    .map((key) => ({
        label: FRONTAGE_FILTER_LABELS[key],
        value: filters[key],
    }));

const buildRefineSearchEventProperties = (filters, comparableType) => {
    const priceLabel = `${LABELS[comparableType]} Price`;

    return [
        {label: 'Property Type', value: buildPropertyTypeProperties(filters.propertyType)},
        {label: 'Listing Type', value: buildListingTypeProperties(filters.listingType)},
        {label: 'Distance', value: filters.distance},
        {label: 'Target Suburb', value: filters.targetSuburbOnly},
        buildExcludeAAProperty(comparableType, filters.excludeRecentlyAdvisedSales),
        buildExcludeRelatedPartiesProperty(comparableType, filters.excludeRelatedParties),
        buildShowActiveListingsForSaleProperty(comparableType, filters.isActiveForSaleCampaign),
        buildShowActiveListingsForRentProperty(comparableType, filters.isActiveForRentCampaign),
        buildRentalFrequencyProperty(filters.rentalPriceFrequency, comparableType),
        buildMultiselectProperty(filters.propertySubType, 'Property Sub Type'),
        buildMultiselectProperty(filters.landUse, 'Land Use'),
        buildMultiselectProperty(filters.devZone, 'Development Zone'),
        buildKeywordProperty(filters.keywords),
        ...buildRangeFilterProperty(filters.beds, 'Bed Min', 'Bed Max'),
        ...buildRangeFilterProperty(filters.baths, 'Bath Min', 'Bath Max'),
        ...buildRangeFilterProperty(filters.cars, 'Car Min', 'Car Max'),
        ...buildRangeFilterProperty(filters.price, `${priceLabel} Min`, `${priceLabel} Max`),
        ...buildRangeFilterPropertyWithUnit(filters.landArea, 'Land Size Min', 'Land Size Max', 'Land Size Unit'),
        ...buildRangeFilterProperty(filters.floorArea, 'Floor Area Min', 'Floor Area Max'),
        ...buildDateRangeFilterProperty(comparableType, filters.date),
        ...buildGenericDateRangeFilterProperty(filters.settlementDate, 'Settlement Date'),
        ...buildGenericDateRangeFilterProperty(filters.firstListingDate, 'First Listing Date'),
        ...buildGenericDateRangeFilterProperty(filters.rentalLastCampaignStartDate, 'First Rental Date'),
        ...buildRangeFilterProperty(filters.yearBuilt, 'Year Built Min', 'Year Built Max'),
        ...buildCommercialFilterProperty(filters),
        ...buildDistanceFilterProperty(filters),
        ...buildExternalFilterProperty(filters),
        ...buildFrontageFilterProperty(filters),
        ...buildInternalFilterProperty(filters),
        ...(isNZLocale() ? buildPropertyValuesFilterPropertyNz(filters) : buildPropertyValuesFilterProperty(filters)),
        ...buildRuralFilterProperty(filters),
        ...buildServiceFilterProperty(filters),
        ...buildStructureFilterProperty(filters),

    ].filter(Boolean);
};

export const trackRefineSearch = (comparableType, id, filters) => {
    AnalyticsGateway.sendEvent({
        eventName: 'Refine Search',
        properties: [
            {label: AnalyticsProperties.EVENT_PROPERTIES.PAGE, value: `Comparable ${LABELS[comparableType]}s`},
            {label: AnalyticsProperties.EVENT_PROPERTIES.PROPERTY_ID, value: id},
            ...buildRefineSearchEventProperties(filters[comparableType], comparableType),
        ]
    });
};

export const trackResetFiltersEvent = (comparableType, id) => {
    AnalyticsGateway.sendEvent({
        eventName: 'Reset Filters',
        properties: [
            {label: AnalyticsProperties.EVENT_PROPERTIES.PAGE, value: `Comparable ${LABELS[comparableType]}s`},
            {label: AnalyticsProperties.EVENT_PROPERTIES.PROPERTY_ID, value: id}
        ]
    });
};

export const trackAdvancedFilters = (comparableType, id) => {
    AnalyticsGateway.sendEvent({
        eventName: 'Advanced Filters',
        properties: [
            {label: AnalyticsProperties.EVENT_PROPERTIES.PAGE, value: `Comparable ${LABELS[comparableType]}s`},
            {label: AnalyticsProperties.EVENT_PROPERTIES.PROPERTY_ID, value: id},
        ],
    });
};